import { useEffect, useState } from "react";
import { SuccessFetch } from "types";
import { useApi } from "utils";
import { InventoryTurnoverResponse, TurnoverRule } from "../../types/InventoryTurnover";

type FillterParams= {
  year_month: string;
  location_id?: string;
  category_id?: string;
  page?: number;
  limit?: number;
};

type DropwdownTurnover = {
    label: string;
    value: string;
}

type PaginationType = {
  total: number
  page: number
  size: number
  pages: number
}

export const useInventoryTurnover = ({ year_month, location_id, category_id, page, limit }: FillterParams) => {
  const api = useApi();
  const [data, setData] = useState<InventoryTurnoverResponse | null>(null);
  const [pagination, setPaginationData] = useState<PaginationType | null>(null);
  const [isLoading, setLoading] = useState(false)

  const params: Record<string, string | number> = {
    year_month: year_month,
    ...(location_id && { location_id }),
    ...(category_id && { category_id }),
    ...(page && { page }),
    ...(limit && { limit }),
  };

  const fetchData = () => {
    setLoading(true)
    api.get("/dasboard_inventory/dashboard-inventory-turnover", { params })
    .then((res: SuccessFetch<{ payload: InventoryTurnoverResponse, pagination_data: PaginationType }>) => {
      const payload = res.data.payload;

      const normalizedData = {
        ...payload,
        data: payload.data.map(item => ({
          ...item,
          qty_turnover: roundToTwoDecimals(item.qty_turnover)
        }))
      };

      setData(normalizedData);
      setPaginationData(res.data.pagination_data);
    })
    .catch(() => setData(null))
    .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchData();
    return () => {
      setData(null)
      setPaginationData(null)
    };

    // eslint-disable-next-line
  }, [year_month, location_id, category_id, page, limit]);

  const fetchAll = async () => {
    const res = await api.get(
      "/dasboard_inventory/dashboard-inventory-turnover",
      {
        params: {
          year_month,
          ...(location_id && { location_id }),
          ...(category_id && { category_id }),
          page: 1,
          limit: -1
        }
      }
    );
  
    const payload:InventoryTurnoverResponse = res.data.payload;
  
    const normalizedData = {
      ...payload,
      data: payload.data.map(item => ({
        ...item,
        qty_turnover: roundToTwoDecimals(item.qty_turnover)
      }))
    };
  
    return normalizedData; 
  };

  return { 
      fetchData, 
      data , 
      isLoading,
      dropdownTurnover : data?.turnover_rule.map((item) => ({
          label: item.mr_product_turnover_name,
          value: item.mr_product_turnover_code
      })) as DropwdownTurnover[],
      pagination,
      fetchAll
  };
}

function roundToTwoDecimals(value: number): number {
  return Math.round(value * 100) / 100;
}


export function getTurnoverStatus(
  qtyTurnover: number,
  rules: TurnoverRule[]
) {
  if (!rules || rules.length === 0) return undefined;

  // sort rules dari min terkecil ke terbesar
  const sortedRules = [...rules].sort(
    (a, b) => a.min_value - b.min_value
  );

  // loop dan bandingkan dengan min rule berikutnya
  for (let i = 0; i < sortedRules.length; i++) {
    const currentRule = sortedRules[i];
    const nextRule = sortedRules[i + 1];

    // kalau tidak ada rule berikutnya → rule terakhir
    if (!nextRule) {
      return currentRule;
    }

    // kalau qty masih di bawah min rule berikutnya
    if (qtyTurnover < nextRule.min_value) {
      return currentRule;
    }
  }

  return undefined;
}

export function filterByStatus<T extends { qty_turnover: number }>(
  data: T[],
  rules: TurnoverRule[],
  status?: string
) {
  if (!status) return data;
  return data.filter(item => {
    const rule = getTurnoverStatus(item.qty_turnover, rules);
    return rule?.mr_product_turnover_code === status;
  });
}